import React, { useState, useEffect, useRef } from 'react';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import ImageIcon from '@material-ui/icons/Image';
import MovieIcon from '@material-ui/icons/Movie';
import DescriptionIcon from '@material-ui/icons/Description';
import GridOnIcon from '@material-ui/icons/GridOn';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { getOrgDocuments, downloadOrgDocument, fetchOrgDocumentBlob } from '../api/api';
import './ResourcesPage.css';


interface OrgDocument {
    id: number;
    title: string;
    file_type: string;
    file_size: number;
    date_uploaded: string;
}

interface ResourcesPageProps {
    domain: string;
    accessToken: string;
}

const ResourcesPage: React.FC<ResourcesPageProps> = ({ domain, accessToken }) => {
    const [documents, setDocuments] = useState<OrgDocument[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Inline preview state
    const [previewDoc, setPreviewDoc] = useState<OrgDocument | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const blobUrlRef = useRef<string | null>(null);
    const previewRequestId = useRef<number>(0);

    // Revoke blob URL on unmount
    useEffect(() => {
        return () => {
            if (blobUrlRef.current) window.URL.revokeObjectURL(blobUrlRef.current);
        };
    }, []);

    const getApiErrorMessage = (err: unknown, fallback: string) => {
        if (typeof err === 'object' && err !== null) {
            const maybeErr = err as {
                message?: string;
                response?: { data?: { message?: string } };
            };
            return maybeErr.response?.data?.message || maybeErr.message || fallback;
        }
        return fallback;
    };

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        if (!domain || !accessToken) {
            setError('Missing domain or access token');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await getOrgDocuments(domain, accessToken);
            if (response.status === 'success') {
                setDocuments(response.documents || []);
            } else {
                setError(response.message || 'Failed to fetch documents');
            }
        } catch (err) {
            setError(getApiErrorMessage(err, 'Error fetching documents'));
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (doc: OrgDocument) => {
        try {
            await downloadOrgDocument(domain, accessToken, doc.id, doc.title);
        } catch (err) {
            setError('Download failed');
            setTimeout(() => setError(null), 5000);
        }
    };

    const handlePreview = async (doc: OrgDocument) => {
        if (blobUrlRef.current) { window.URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
        setPreviewDoc(doc); setPreviewUrl(null); setPreviewLoading(true); setError(null);
        const requestId = ++previewRequestId.current;
        try {
            const url = await fetchOrgDocumentBlob(domain, accessToken, doc.id);
            if (requestId !== previewRequestId.current) {
                window.URL.revokeObjectURL(url);
                return;
            }
            blobUrlRef.current = url;
            setPreviewUrl(url);
        } catch (err) {
            if (requestId !== previewRequestId.current) return;
            setError(getApiErrorMessage(err, 'Failed to load preview'));
            setPreviewDoc(null);
        } finally {
            if (requestId === previewRequestId.current) setPreviewLoading(false);
        }
    };

    const handleClosePreview = () => {
        previewRequestId.current++;
        if (blobUrlRef.current) { window.URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
        setPreviewDoc(null); setPreviewUrl(null); setPreviewLoading(false);
    };


    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
    };

    const canPreview = (ft: string) =>
        ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'mp4', 'mov', 'avi', 'wmv', 'mkv', 'webm'].includes(ft);

    const getFileIcon = (ft: string) => {
        if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(ft)) return <ImageIcon className="doc-icon" />;
        if (['mp4', 'mov', 'avi', 'wmv', 'mkv', 'webm'].includes(ft)) return <MovieIcon className="doc-icon" />;
        if (ft === 'pdf') return <PictureAsPdfIcon className="doc-icon" />;
        if (['doc', 'docx'].includes(ft)) return <DescriptionIcon className="doc-icon" />;
        if (['xls', 'xlsx'].includes(ft)) return <GridOnIcon className="doc-icon" />;
        return <AttachFileIcon className="doc-icon" />;
    };

    const renderPreviewContent = () => {
        if (!previewDoc || !previewUrl) return null;
        const { file_type, title } = previewDoc;
        if (['jpg', 'jpeg', 'png', 'gif'].includes(file_type))
            return <img src={previewUrl} alt={title} style={{ maxWidth: '100%', maxHeight: '80vh', display: 'block', margin: '0 auto' }} />;
        if (file_type === 'pdf')
            return (
                <div style={{ resize: 'vertical', overflow: 'auto', height: '80vh', minHeight: '300px' }}>
                    <iframe src={previewUrl} style={{ width: '100%', height: '100%', border: 'none', display: 'block' }} title={title} />
                </div>
            );
        if (['mp4', 'mov', 'avi', 'wmv', 'mkv', 'webm'].includes(file_type))
            return <video controls src={previewUrl} style={{ width: '100%', maxHeight: '80vh' }}>Your browser does not support video playback.</video>;
        return null;
    };

    return (
        <div className="resources-container">
            <h2>Resources</h2>

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="documents-tab">
                <h3>Organization Documents</h3>
                {loading ? (
                    <p>Loading documents...</p>
                ) : documents.length === 0 ? (
                    <p className="text-muted">No documents available</p>
                ) : (
                        <>
                            <div className="documents-grid">
                                {documents.map((doc) => (
                                    <div key={doc.id} className={`document-card${previewDoc?.id === doc.id ? ' document-card--active' : ''}`}>
                                        <div className="doc-header">
                                            {getFileIcon(doc.file_type)}
                                            <span className="doc-type">{doc.file_type.toUpperCase()}</span>
                                        </div>
                                        <h5 className="doc-title">{doc.title}</h5>
                                        <p className="doc-meta">
                                            {formatFileSize(doc.file_size)}
                                            <br />
                                            {formatDate(doc.date_uploaded)}
                                        </p>
                                        <div className="doc-actions">
                                            {canPreview(doc.file_type) && (
                                                <button
                                                    className="btn btn-sm btn-info"
                                                    onClick={() => previewDoc?.id === doc.id ? handleClosePreview() : handlePreview(doc)}
                                                    disabled={previewLoading && previewDoc?.id !== doc.id}
                                                >
                                                    {previewDoc?.id === doc.id ? 'Close' : 'Preview'}
                                                </button>
                                            )}
                                            <button
                                                className="btn btn-sm btn-primary"
                                                onClick={() => handleDownload(doc)}
                                            >
                                                Download
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {previewDoc && (
                                <div className="preview-section">
                                    <div className="preview-section-header">
                                        <span className="preview-section-title">{previewDoc.title}</span>
                                        <div className="preview-section-actions">
                                            <button className="btn btn-sm btn-primary" onClick={() => handleDownload(previewDoc)}>Download</button>
                                            <button className="btn btn-sm btn-secondary" onClick={handleClosePreview}>Close</button>
                                        </div>
                                    </div>
                                    <div className="preview-section-body">
                                        {previewLoading
                                            ? <p className="preview-loading">Loading preview...</p>
                                            : renderPreviewContent()
                                        }
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

        </div>
    );
};

export default ResourcesPage;
